import { useState, useEffect } from 'react';

const BASE = '/api';
function api(method, path, body) {
  const token = localStorage.getItem('ozon_token');
  return fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || r.statusText);
    return d;
  });
}

function fmtDt(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString('tr-TR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
}

const STATUS = {
  draft:     { label: 'Taslak',     color: 'bg-gray-100 text-gray-600' },
  sent:      { label: 'Gönderildi', color: 'bg-blue-100 text-blue-700' },
  approved:  { label: 'Onaylandı',  color: 'bg-green-100 text-green-700' },
  rejected:  { label: 'Reddedildi', color: 'bg-red-100 text-red-700' },
  failed:    { label: 'Başarısız',  color: 'bg-orange-100 text-orange-700' },
  cancelled: { label: 'İptal',      color: 'bg-gray-100 text-gray-400' },
};

function Badge({ status }) {
  const s = STATUS[status] || STATUS.draft;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>{s.label}</span>;
}

// Kaynak ayarları modal
function SourceSettingsModal({ source, onClose, onSaved }) {
  const [form, setForm] = useState({
    endpoint_url: source.endpoint_url || '',
    api_key:      source.api_key || '',
    callback_key: source.callback_key || '',
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api('PATCH', `/stock-requests/sources/${encodeURIComponent(source.name)}`, form);
      onSaved();
      onClose();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-1">{source.name} — Bağlantı Ayarları</h2>
        <p className="text-xs text-gray-400 mb-5">Karşı tarafın backend adresi ve API key'leri</p>
        {[
          { key: 'endpoint_url', label: 'Endpoint URL', placeholder: 'http://localhost:5002' },
          { key: 'api_key',      label: 'API Key',      placeholder: 'gizli-anahtar' },
          { key: 'callback_key', label: 'Callback Key', placeholder: 'callback-anahtar' },
        ].map(f => (
          <div key={f.key} className="mb-3">
            <label className="text-xs font-semibold text-gray-500 block mb-1">{f.label}</label>
            <input
              value={form[f.key]}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
            />
          </div>
        ))}
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2 text-sm hover:bg-gray-50">İptal</button>
          <button onClick={save} disabled={saving} className="flex-1 bg-blue-600 text-white rounded-xl py-2 text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Talep detay modal
function RequestDetail({ request, onClose, onUpdate }) {
  const [sending,   setSending]   = useState(false);
  const [cancelling, setCancelling] = useState(false);

  async function send() {
    setSending(true);
    try {
      const updated = await api('POST', `/stock-requests/${request.id}/send`);
      onUpdate(updated);
      if (updated.warning) alert('⚠️ ' + updated.warning);
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setSending(false); }
  }

  async function cancel() {
    if (!window.confirm('Talep iptal edilsin mi?')) return;
    setCancelling(true);
    try {
      const updated = await api('POST', `/stock-requests/${request.id}/cancel`);
      onUpdate(updated);
      onClose();
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setCancelling(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Başlık */}
        <div className="p-5 border-b border-gray-100 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-bold text-gray-900">{request.source_name} Stok Talebi</h2>
              <Badge status={request.status} />
            </div>
            <p className="text-xs text-gray-400">
              {request.session_note || 'Oturum'} · {fmtDt(request.created_at)}
              {request.created_by && ` · ${request.created_by}`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-300 hover:text-gray-600 text-xl p-1">✕</button>
        </div>

        {/* Ürün listesi */}
        <div className="flex-1 overflow-y-auto p-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 border-b border-gray-100">
                <th className="text-left py-2 font-medium">Ürün Kodu</th>
                <th className="text-left py-2 font-medium">Ürün Adı</th>
                <th className="text-right py-2 font-medium">Adet</th>
              </tr>
            </thead>
            <tbody>
              {request.items.map((item, i) => (
                <tr key={i} className="border-b border-gray-50">
                  <td className="py-2.5 pr-4">
                    <div className="font-mono text-xs font-medium text-gray-800">{item.offer_id}</div>
                  </td>
                  <td className="py-2.5 pr-4">
                    <div className="text-xs text-gray-500 truncate max-w-[200px]">{item.name}</div>
                  </td>
                  <td className="py-2.5 text-right font-semibold text-gray-800">{item.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {request.response_note && (
            <div className={`mt-4 rounded-xl px-4 py-3 text-sm border ${
              request.status === 'approved' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              <p className="font-semibold mb-0.5">Karşı taraf notu:</p>
              <p>{request.response_note}</p>
              {request.responded_at && <p className="text-xs opacity-60 mt-1">{fmtDt(request.responded_at)}</p>}
            </div>
          )}
        </div>

        {/* Aksiyonlar */}
        {request.status === 'draft' && (
          <div className="p-5 border-t border-gray-100 flex gap-2">
            <button
              onClick={cancel}
              disabled={cancelling}
              className="border border-gray-200 text-gray-600 rounded-xl px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              İptal Et
            </button>
            <button
              onClick={send}
              disabled={sending}
              className="flex-1 bg-blue-600 text-white rounded-xl px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {sending ? 'Gönderiliyor...' : `${request.source_name}'e Gönder`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function StockRequests() {
  const [requests, setRequests] = useState([]);
  const [sources,  setSources]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [detail,   setDetail]   = useState(null);
  const [settings, setSettings] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [reqs, srcs] = await Promise.all([
        api('GET', '/stock-requests'),
        api('GET', '/stock-requests/sources'),
      ]);
      setRequests(reqs);
      setSources(srcs);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function updateRequest(updated) {
    setRequests(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
    if (detail?.id === updated.id) setDetail(d => ({ ...d, ...updated }));
  }

  const counts = Object.fromEntries(
    Object.keys(STATUS).map(s => [s, requests.filter(r => r.status === s).length])
  );

  return (
    <div className="p-4 md:p-8 max-w-4xl">
      {/* Başlık */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stok Talepleri</h1>
          <p className="text-sm text-gray-500 mt-1">Tedarikçilere gönderilen stok düşüm talepleri</p>
        </div>
        <div className="flex gap-2">
          {sources.map(s => (
            <button key={s.name}
              onClick={() => setSettings(s)}
              className="text-xs border border-gray-200 text-gray-500 hover:border-gray-300 px-3 py-1.5 rounded-lg"
            >
              ⚙ {s.name}
            </button>
          ))}
        </div>
      </div>

      {/* Özet kartları */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6">
        {Object.entries(STATUS).map(([key, s]) => (
          <div key={key} className={`rounded-xl px-3 py-2 text-center border ${counts[key] > 0 ? s.color.replace('text-', 'border-').replace(/\d+$/, '200') : 'border-gray-100 bg-gray-50'}`}>
            <div className={`text-lg font-bold ${counts[key] > 0 ? s.color.split(' ')[1] : 'text-gray-300'}`}>{counts[key]}</div>
            <div className="text-xs text-gray-400">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-16 text-gray-300">Yükleniyor...</div>
      ) : requests.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <div className="text-4xl mb-3">📬</div>
          <p className="text-sm font-medium">Henüz talep yok</p>
          <p className="text-xs mt-1">Tedarik oturumu detayından "Stok Talebi Oluştur" butonunu kullan</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          {requests.map((r, i) => (
            <button key={r.id}
              onClick={() => setDetail(r)}
              className={`w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors ${i < requests.length - 1 ? 'border-b border-gray-50' : ''}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-gray-900 text-sm">{r.source_name}</span>
                  <Badge status={r.status} />
                  <span className="text-xs text-gray-400">{r.items?.length || 0} ürün</span>
                </div>
                <p className="text-xs text-gray-400 truncate">
                  {r.session_note || 'Oturum'} · {fmtDt(r.created_at)}
                  {r.created_by ? ` · ${r.created_by}` : ''}
                </p>
              </div>
              <div className="text-gray-300 flex-shrink-0">›</div>
            </button>
          ))}
        </div>
      )}

      {detail   && <RequestDetail request={detail} onClose={() => setDetail(null)} onUpdate={updateRequest} />}
      {settings && <SourceSettingsModal source={settings} onClose={() => setSettings(null)} onSaved={load} />}
    </div>
  );
}
